import { Point } from "GenAlphaModel";
import type { InteractionHost } from "../interaction_host";
import { ModeHandler } from "./base";
import { DragZoom } from "./drag_zoom";

// # Panning Mode
//
// Moving the schematic canvas around while holding down the mouse.
//
export class Pan extends ModeHandler {
  // Internal data
  constructor(
    readonly editor: InteractionHost,
    // Mouse position at mode entry, in schematic coordinates
    readonly initialSchPos: Point,
    // Mode to restore on exit (defaults to idle if not provided)
    private readonly returnMode?: ModeHandler
  ) {
    super();
  }

  // Start Panning Mode.
  // returnMode: if provided, exit() restores this mode instead of going idle.
  static start(editor: InteractionHost, returnMode?: ModeHandler) {
    return new Pan(
      editor,
      Point.clone(editor.uiState.mousePos.sch),
      returnMode
    );
  }

  // Abort the in-progress operation and return to the `Idle` mode.
  // FIXME: do we snap back or leave the position as is?
  // As is, we leave the panned position.
  override abort = () => {
    this.exit();
  };

  // This is the "natural" exit; the user has released the mouse button.
  override handleMouseUp = () => {
    this.exit();
  };

  // FIXME: these "mouse down time" events can happen if the user clicks on the canvas
  // and then releases off-screen. We should probably do something about that.
  // Right-click while panning: transition to drag-zoom mode.
  override handleMouseDown = (e: MouseEvent) => {
    if (e.button === 2) {
      this.editor.modeHandler = DragZoom.start(this.editor);
    }
  };
  override handleDoubleClick = () => {}; // Can't happen
  override handleKey = (_e: KeyboardEvent) => {}; // FIXME: Do nothing(?)

  // This one is where the action happens.
  // Update the canvas position on mouse-move.
  override handleMouseMove = () => {
    const { initialSchPos } = this;
    const { uiState, canvas } = this.editor;
    const { mousePos } = uiState;
    const { scale } = canvas.worldTransforms.schToPage;

    // Pan such that the same point on the schematic
    // that was under the cursor at the start of the pan
    // remains under the cursor during the pan.
    // That is, we want to satisfy:
    // so `schToPage1(initialSchPos) == mousePos.page`
    // Note this holds even if the scale has changed during panning.
    const offset = {
      x: mousePos.page.x - scale * initialSchPos.x,
      y: mousePos.page.y + scale * initialSchPos.y,
    };
    const newXform = { scale, offset };
    canvas.updateWorldTransforms(newXform, "schToPage");
  };

  // Exit panning mode, keeping the new position.
  // Returns to returnMode if provided, otherwise goes idle.
  exit = () => {
    // Grid is now viewport-computed per frame, no explicit update needed.
    if (this.returnMode) {
      this.editor.setMode(this.returnMode);
    } else {
      this.editor.goUiIdle();
    }
  };
}
